import { some } from 'lodash';
import { GraphNodeTypes } from '../../../services/ducks/designerDuck/designerDuck';
import { WorkflowStateNode } from '../../../services/ducks/workflowDuck/workflowDuck';

export function canAddState(
  nodeId: string,
  graphItems: { [key: string]: WorkflowStateNode },
  grabbedItemId: WorkflowStateNode['id']
): boolean {
  const currentNode = graphItems[nodeId];
  const nextNodes = currentNode.next.map(
    (nextId: string) => graphItems[nextId]
  );

  interface RunModulesUUIDDictionary {
    [key: string]: boolean;
  }
  const grabbedItemRunModuleUUIDs: RunModulesUUIDDictionary = {};
  if (currentNode.pluginUUID) {
    if (grabbedItemId in graphItems) {
      graphItems[grabbedItemId].runModules?.forEach(runModule => {
        if (runModule?.pluginUUID) {
          grabbedItemRunModuleUUIDs[runModule.pluginUUID] = true;
        }
      });

      if (grabbedItemRunModuleUUIDs[currentNode.pluginUUID]) {
        return false;
      }
    }
  }

  switch (currentNode.type) {
    case GraphNodeTypes.TRIGGER:
      return true;
    case GraphNodeTypes.STATE:
      if (some(nextNodes, ['type', GraphNodeTypes.MODULE])) {
        return false;
      }
      return true;
    case GraphNodeTypes.MODULE:
      if (some(nextNodes, ['type', GraphNodeTypes.MODULE])) {
        return false;
      }
      return true;
    case GraphNodeTypes.JUNCTION:
      return true;
    case GraphNodeTypes.CHILD:
      return true;
    case GraphNodeTypes.GROUP_JUNCTION:
      return false;
    case GraphNodeTypes.GROUP_EXIT:
      return true;
    default: {
      const _exhaustiveCheck: never = currentNode.type;
      return _exhaustiveCheck;
    }
  }
}

export function canAddJunction(
  nodeId: string,
  graphItems: { [key: string]: WorkflowStateNode }
): boolean {
  const currentNode = graphItems[nodeId];
  const nextNodes = currentNode.next.map(
    (nextId: string) => graphItems[nextId]
  );
  switch (currentNode.type) {
    case GraphNodeTypes.TRIGGER:
      return true;
    case GraphNodeTypes.STATE:
      if (some(nextNodes, ['type', GraphNodeTypes.MODULE])) {
        return false;
      }
      return true;
    case GraphNodeTypes.MODULE:
      if (some(nextNodes, ['type', GraphNodeTypes.MODULE])) {
        return false;
      }
      return true;
    case GraphNodeTypes.JUNCTION:
      if (
        some(
          nextNodes,
          node => node.type === GraphNodeTypes.STATE && node.isJunctionState
        )
      ) {
        return false;
      }
      return true;
    case GraphNodeTypes.CHILD:
      return true;
    case GraphNodeTypes.GROUP_JUNCTION:
      return false;
    case GraphNodeTypes.GROUP_EXIT:
      return true;
    default: {
      const _exhaustiveCheck: never = currentNode.type;
      return _exhaustiveCheck;
    }
  }
}

export function canAddChildWorkflow(
  nodeId: string,
  graphItems: { [key: string]: WorkflowStateNode }
): boolean {
  const currentNode = graphItems[nodeId];
  const nextNodes = currentNode.next.map(
    (nextId: string) => graphItems[nextId]
  );
  switch (currentNode.type) {
    case GraphNodeTypes.TRIGGER:
      return true;
    case GraphNodeTypes.STATE:
      if (some(nextNodes, ['type', GraphNodeTypes.MODULE])) {
        return false;
      }
      return true;
    case GraphNodeTypes.MODULE:
      if (some(nextNodes, ['type', GraphNodeTypes.MODULE])) {
        return false;
      }
      return true;
    case GraphNodeTypes.JUNCTION:
      if (
        some(
          nextNodes,
          node => node.type === GraphNodeTypes.STATE && node.isJunctionState
        )
      ) {
        return false;
      }
      return true;
    case GraphNodeTypes.CHILD:
      return true;
    case GraphNodeTypes.GROUP_JUNCTION:
      return false;
    case GraphNodeTypes.GROUP_EXIT:
      return true;
    default: {
      const _exhaustiveCheck: never = currentNode.type;
      return _exhaustiveCheck;
    }
  }
}

export function canAddModule(
  nodeId: string,
  graphItems: { [key: string]: WorkflowStateNode }
): boolean {
  const currentNode = graphItems[nodeId];
  switch (currentNode.type) {
    case GraphNodeTypes.TRIGGER:
      return false;
    case GraphNodeTypes.STATE:
      return true;
    case GraphNodeTypes.MODULE:
      return true;
    case GraphNodeTypes.JUNCTION:
      return false;
    case GraphNodeTypes.CHILD:
      return false;
    case GraphNodeTypes.GROUP_JUNCTION:
      return false;
    case GraphNodeTypes.GROUP_EXIT:
      return false;
    default: {
      const _exhaustiveCheck: never = currentNode.type;
      return _exhaustiveCheck;
    }
  }
}

export function canAddGroup(
  nodeId: string,
  graphItems: { [key: string]: WorkflowStateNode }
) {
  const currentNode = graphItems[nodeId];
  const nextNodes = currentNode.next.map(
    (nextId: string) => graphItems[nextId]
  );
  switch (currentNode.type) {
    case GraphNodeTypes.TRIGGER:
      return false;
    case GraphNodeTypes.STATE:
      if (some(nextNodes, ['type', GraphNodeTypes.MODULE])) {
        return false;
      }
      return true;
    case GraphNodeTypes.MODULE:
      if (some(nextNodes, ['type', GraphNodeTypes.MODULE])) {
        return false;
      }
      return true;
    case GraphNodeTypes.JUNCTION:
      return false;
    case GraphNodeTypes.CHILD:
      return false;
    case GraphNodeTypes.GROUP_JUNCTION:
      return false;
    case GraphNodeTypes.GROUP_EXIT:
      return false;
    default: {
      const _exhaustiveCheck: never = currentNode.type;
      return _exhaustiveCheck;
    }
  }
}

export const isCurrentNodeOrChildToCurrentNode = (
  nodeId: string,
  grabbedNodeId: string,
  graphItems: { [key: string]: WorkflowStateNode }
) => {
  const nextNodes = graphItems[nodeId].next.map(
    (nextId: string) => graphItems[nextId]
  );
  return (
    nodeId === grabbedNodeId ||
    some(nextNodes, node => node.id === grabbedNodeId)
  );
};

export const GraphNodeTypeLabels = {
  [GraphNodeTypes.TRIGGER]: 'Trigger',
  [GraphNodeTypes.STATE]: 'State',
  [GraphNodeTypes.MODULE]: 'Module',
  [GraphNodeTypes.JUNCTION]: 'Junction',
  [GraphNodeTypes.GROUP_JUNCTION]: 'Group Junction',
  [GraphNodeTypes.GROUP_EXIT]: 'Group Exit',
  [GraphNodeTypes.CHILD]: 'Child Workflow',
};
